import React, { useCallback, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import { HeroBanner } from "../../Common/HeroBanner";
import { getBlogFiltersApi, getBlogListApi } from "../../Service/blog";
import { BLOG, BLOG_DETAIL } from "../../Routes/RouteConstent";
import BlogNoResults from "./BlogNoResults";
import BlogStayInLoopCTA from "./BlogStayInLoopCTA";
import BlogToolbar from "./BlogToolbar";
import BlogLoading from "./BlogLoading";
import blogHero from "../../Assets/img/blog/blog-hero.png";
import blogHeroMobile from "../../Assets/img/blog/blog-hero-mobile.png";
import { useBlogPageMeta } from "./useBlogPageMeta";
import { getBlogCardImage } from "./blogImageUtils";
import useDebounce from "../../Hooks/useDebounce";
import "./Blog.scss";

const BlogList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogs, setBlogs] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [stages, setStages] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");
  const debouncedSearchInput = useDebounce(searchInput, 350);

  const category = searchParams.get("category") || "";
  const stage = searchParams.get("stage") || "";
  const search = searchParams.get("search") || "";
  const page = searchParams.get("page") || "1";
  const hasFilters = Boolean(category || stage || search);
  const isFilteredEmpty = !loading && blogs.length === 0 && hasFilters;

  useBlogPageMeta({
    title: "Blog | Nurture by NAPS™",
    description:
      "Parenting tips, newborn support, sleep, and feeding guidance from Nurture by NAPS™ registered nurses.",
    canonicalPath: BLOG,
    image: blogHero,
  });

  const loadFilters = useCallback(async () => {
    try {
      const res: any = await getBlogFiltersApi();
      if (res?.data?.success) {
        setCategories(res.data.categories || []);
        setStages(res.data.stages || []);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const loadBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (category) params.category = category;
      if (stage) params.stage = stage;
      if (search) params.search = search;
      if (page === "all") {
        params.page = "all";
      } else {
        params.page = Number(page) || 1;
        params.per_page = 9;
      }

      const res: any = await getBlogListApi(params);
      if (res?.data?.success) {
        setBlogs(res.data.blogs || []);
        setPagination(res.data.pagination);
      }
    } catch (e) {
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, [category, stage, search, page]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadBlogs();
  }, [loadBlogs]);

  useEffect(() => {
    const current = searchParams.get("search") || "";
    const nextValue = debouncedSearchInput.trim();
    if (current === nextValue) return;
    const next = new URLSearchParams(searchParams);
    if (nextValue) next.set("search", nextValue);
    else next.delete("search");
    next.delete("page");
    setSearchParams(next, { replace: true });
  }, [debouncedSearchInput, searchParams, setSearchParams]);


  const applyFilters = (filters: { category: string[]; stage: string[] }) => {
    const next = new URLSearchParams(searchParams);
    const cat = filters.category[0];
    const st = filters.stage[0];
    if (cat) next.set("category", cat);
    else next.delete("category");
    if (st) next.set("stage", st);
    else next.delete("stage");
    next.delete("page");
    setSearchParams(next);
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearchParams({});
  };

  const renderCard = (blog: any) => {
    const to = blog.external_url || BLOG_DETAIL.replace(":slug", blog.slug);
    const isExternal = Boolean(blog.external_url);
    const Wrapper: any = isExternal ? "a" : Link;
    const wrapperProps = isExternal
      ? { href: to, target: "_blank", rel: "noreferrer" }
      : { to };

    return (
      <Wrapper {...wrapperProps} className="blog-card-link">
        <article className="blog-card">
          <div
            className="blog-img"
            style={{ backgroundImage: `url(${getBlogCardImage(blog)})` }}
            role="img"
            aria-label={blog.title}
          />
          <div className="blog-content">
            <span className="creation-date">{blog.days}</span>
            <h3>{blog.title}</h3>
            <p className="blog-summary">{blog.summary}</p>
            <span className="read-more">Read More</span>
          </div>
        </article>
      </Wrapper>
    );
  };

  return (
    <div className="blog-page">
      <Container fluid className="blog-container">
        <div className="blog-hero-slot">
          <HeroBanner
            hero={blogHero}
            heroMobile={blogHeroMobile}
            className="blog-hero-banner"
            title={
              <>
                Our <span className="strong">Blog</span>
              </>
            }
          />
        </div>
      </Container>

      <Container className="blog-layout">
        <BlogToolbar
          search={searchInput}
          onSearchChange={setSearchInput}
          category={category}
          stage={stage}
          categories={categories}
          stages={stages}
          onApplyFilters={applyFilters}
          onClearFilters={clearFilters}
        />

        {loading ? (
          <BlogLoading label="Loading articles…" />
        ) : isFilteredEmpty ? (
          <BlogNoResults onReset={clearFilters} />
        ) : blogs.length === 0 ? (
          <section className="blog-empty-state blog-empty-state--plain" aria-live="polite">
            <div className="blog-empty-state__card">
              <h2 className="blog-empty-state__title">No articles yet.</h2>
              <p className="blog-empty-state__lead">Check back soon for new stories from our nurses.</p>
            </div>
          </section>
        ) : (
          <>
            <section className="blog-grid">
              {blogs.map((blog) => (
                <div key={blog.id} className="blog-grid-item">
                  {renderCard(blog)}
                </div>
              ))}
            </section>

            {!loading && pagination && pagination.last_page > 1 && page !== "all" && (
              <div className="blog-more">
                <button
                  type="button"
                  className="primary-blue-small-btn"
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.set("page", "all");
                    setSearchParams(next);
                  }}
                >
                  View all articles
                </button>
              </div>
            )}
          </>
        )}
      </Container>

      <BlogStayInLoopCTA variant="teaser" />
    </div>
  );
};

export default BlogList;
